import type { IndividualContactId, SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	type AdminAccountIndividual,
	computeAverageFico,
	useAdminRunIndividualCreditReport,
} from "@portal/api/account/admin";
import {
	type FailureCopy,
	failureCopy,
	formatReportDate,
} from "@portal/api/account/admin/individualCreditReport";
import { CreditReportActionsMenu } from "@portal/components/features/account/credit-report";
import { Banner, Icon, PartialIndicator, Text } from "@portal/components/primitives";
import { DynamicDataTable } from "@portal/components/ui/DynamicDataTable/DynamicDataTable";
import { DynamicDataTableColumn } from "@portal/components/ui/DynamicDataTable/types";
import { LineItem } from "@portal/components/ui/LineItem";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { DateTime } from "luxon";
import { FC, useCallback, useId, useMemo, useState } from "react";
import { formatEmpty, formatName } from "../../../../../utils";
import { IndividualCreditReportModal } from "./credit-report/individual/IndividualCreditReportModal";

type IndividualCreditReport = AdminAccountIndividual["creditReports"][number];

type IndividualCreditReportRow = {
	name: string | undefined;
	score: number | undefined;
	failure: FailureCopy | undefined;
	bankruptcy: boolean | undefined;
	createdAt: string | undefined;
	individual: AdminAccountIndividual;
	report: IndividualCreditReport | undefined;
	/** A newer pull that failed while an older successful report stays in use. */
	failedAttempt: IndividualCreditReport | undefined;
};

/** The API sends the latest pull first, then the latest successful one if a newer pull failed. */
const toRow = (individual: AdminAccountIndividual): IndividualCreditReportRow => {
	const [latest] = individual.creditReports;
	const report =
		individual.creditReports.find((candidate) => candidate.outcome.status === "success") ?? latest;
	const success = report?.outcome.status === "success" ? report.outcome : undefined;
	return {
		name: formatName(individual),
		score: success?.score,
		failure:
			report?.outcome.status === "failed"
				? failureCopy(report.outcome.failure, report.vendor)
				: undefined,
		bankruptcy: success?.bankruptcy,
		createdAt: report?.createdAt,
		individual,
		report,
		failedAttempt: latest !== report ? latest : undefined,
	};
};

const FailedAttemptWarning: FC<{ message: string; testId: string }> = ({ message, testId }) => {
	const tooltipId = useId();
	return (
		<>
			<span
				role="img"
				aria-label={message}
				data-tooltip-id={tooltipId}
				data-tooltip-content={message}
				data-testid={testId}
				className="flex"
			>
				<Icon type="exclamation-triangle" size="small" color="warning" className="stroke-[1.75]" />
			</span>
			<PortaledTooltip id={tooltipId} style={{ maxWidth: 260 }} />
		</>
	);
};

const buildIndividualCreditReportColumns = ({
	onRunCreditReport,
	runCreditReportPending,
}: {
	onRunCreditReport: (individualId: IndividualContactId) => void;
	runCreditReportPending: boolean;
}): DynamicDataTableColumn<IndividualCreditReportRow>[] => [
	{
		id: "actions",
		header: "Actions",
		size: 80,
		cell: ({ row }) => (
			<div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
				<CreditReportActionsMenu
					testId={`individual-credit-report-actions-${row.original.individual.id}`}
					confirmMessage={`Pull a new credit report for ${row.original.name ?? "this individual"}? This runs a fresh credit pull right away.`}
					onRunCreditReport={() => onRunCreditReport(row.original.individual.id)}
					runCreditReportPending={runCreditReportPending}
				/>
			</div>
		),
	},
	{
		id: "timestampStatus",
		accessorKey: "createdAt",
		header: "Date Pulled",
		size: 140,
		minSize: 140,
		enableSorting: true,
		cell: ({ row }) => {
			const { createdAt, failedAttempt } = row.original;
			const outcome = row.original.report?.outcome;
			const expired =
				outcome?.status === "success" && DateTime.fromISO(outcome.reusedUntil) < DateTime.now();
			return (
				<div className="flex items-center gap-2">
					<span className="flex size-5 shrink-0 items-center justify-center">
						{createdAt === undefined ? (
							<Icon type="x-circle" size="small" color="secondary" className="stroke-[1.75]" />
						) : failedAttempt?.outcome.status === "failed" ? (
							<FailedAttemptWarning
								message={`Latest pull on ${formatReportDate(failedAttempt.createdAt)} failed: ${failureCopy(failedAttempt.outcome.failure, failedAttempt.vendor).label}`}
								testId={`individual_credit_failed_attempt_${row.original.individual.id}`}
							/>
						) : expired ? (
							<PartialIndicator active />
						) : outcome?.status === "success" ? (
							<Icon type="check-circle" size="small" color="confirm" className="stroke-[1.75]" />
						) : outcome?.status === "failed" && outcome.failure.reason === "frozen" ? (
							<Icon type="snowflake" size="small" color="accent" className="stroke-[1.75]" />
						) : (
							<Icon type="x-mark" size="small" color="danger" filled className="stroke-[1.75]" />
						)}
					</span>
					<span>
						{createdAt !== undefined ? DateTime.fromISO(createdAt).toFormat("MM/dd/yyyy") : "--"}
					</span>
				</div>
			);
		},
	},
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 280,
		minSize: 180,
		enableSorting: true,
		enableResizing: true,
	},
	{
		id: "score",
		accessorKey: "score",
		header: "FICO",
		size: 140,
		enableSorting: true,
		cell: ({ row }) => {
			const { score, failure } = row.original;
			if (Option.isNone(failure)) return formatEmpty(score);
			return (
				<Text
					type="body"
					color={failure.tone === "info" ? "accent" : "danger"}
					title={failure.message}
					data-testid={`individual_credit_failure_${row.original.individual.id}`}
				>
					{failure.label}
				</Text>
			);
		},
	},
	{
		id: "bankruptcy",
		accessorKey: "bankruptcy",
		header: "Bankruptcy",
		size: 120,
		minSize: 120,
		enableSorting: true,
		cell: ({ row }) =>
			row.original.bankruptcy !== undefined ? (row.original.bankruptcy ? "Yes" : "No") : "--",
	},
];

type AdminAccountIndividualsCreditProps = {
	accountId: SuretyAccountId;
	individuals: AdminAccountIndividual[];
	hideHeading?: boolean;
};

const rowLabel = (row: IndividualCreditReportRow) =>
	`Open credit report for ${row.name ?? "individual"}`;

export const AdminAccountIndividualsCredit: FC<AdminAccountIndividualsCreditProps> = ({
	accountId,
	individuals,
	hideHeading = false,
}) => {
	const [selectedId, setSelectedId] = useState<IndividualContactId | undefined>();
	const {
		runIndividualCreditReport,
		runIndividualCreditReportPending,
		runIndividualCreditReportError,
		failedIndividualId,
		resetRunIndividualCreditReport,
	} = useAdminRunIndividualCreditReport(accountId);
	const handleRowClick = useCallback(
		(row: IndividualCreditReportRow) => {
			resetRunIndividualCreditReport();
			setSelectedId(row.individual.id);
		},
		[resetRunIndividualCreditReport],
	);
	const handleClose = useCallback(() => setSelectedId(undefined), []);
	const handleRepull = useCallback(() => {
		if (selectedId) runIndividualCreditReport(selectedId);
	}, [runIndividualCreditReport, selectedId]);
	const underwritingIndividuals = useMemo(
		() => individuals.filter((i) => i.config?.contract.includeInUnderwriting),
		[individuals],
	);

	const individualCreditReportsTableData = useMemo(
		() => underwritingIndividuals.map(toRow),
		[underwritingIndividuals],
	);

	const individualCreditReportColumns = useMemo(
		() =>
			buildIndividualCreditReportColumns({
				onRunCreditReport: runIndividualCreditReport,
				runCreditReportPending: runIndividualCreditReportPending,
			}),
		[runIndividualCreditReport, runIndividualCreditReportPending],
	);

	const averageFico = useMemo(() => computeAverageFico(individuals), [individuals]);
	const selectedRow = individualCreditReportsTableData.find(
		(row) => row.individual.id === selectedId,
	);
	const failedRow = Option.isNone(selectedRow)
		? individualCreditReportsTableData.find((row) => row.individual.id === failedIndividualId)
		: undefined;

	if (individuals.length === 0) {
		return null;
	}

	return (
		<div
			className="flex flex-col space-y-[15px]"
			data-amp-mask
			data-testid="admin_account_individuals_credit"
		>
			{!hideHeading && (
				<Text type="caption" color="tertiary" className="italic">
					Individual Credit Reports
				</Text>
			)}
			{failedRow && (
				<Banner tone="danger" data-testid="individual_credit_run_error">
					<Banner.Leading>
						<Icon type="exclamation-triangle" color="danger" size="small" />
					</Banner.Leading>
					<Banner.Content>
						We couldn't start the credit pull for {failedRow.name ?? "this individual"}. Try again;
						if it keeps failing, contact support.
					</Banner.Content>
				</Banner>
			)}
			<LineItem label="Average FICO" className="px-[20px] pb-[5px]">
				{formatEmpty(averageFico)}
			</LineItem>
			<DynamicDataTable
				name="admin_account_individual_credit_reports"
				tableClassName="shadow-sm"
				columns={individualCreditReportColumns}
				data={individualCreditReportsTableData}
				onRowClick={handleRowClick}
				rowLabel={rowLabel}
				expandToContent
			/>
			{selectedRow && (
				<IndividualCreditReportModal
					name={selectedRow.name ?? "Unnamed individual"}
					report={selectedRow.report}
					failedAttempt={selectedRow.failedAttempt}
					onClose={handleClose}
					onRepull={handleRepull}
					repullPending={runIndividualCreditReportPending}
					repullFailed={Option.isSome(runIndividualCreditReportError)}
				/>
			)}
		</div>
	);
};
